import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useAuth } from '@/features/auth/auth-context'
import {
  acceptTransfer,
  declineTransfer,
  getTransferByToken,
} from '@/features/projects/transfers-api'
import { queryClient } from '@/shared/api/query-client'
import { Button } from '@/shared/components/ui/button'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Select } from '@/shared/components/ui/select'
import { LoadingBlock } from '@/shared/components/common/loading-block'

export function TransferAcceptPage() {
  const { token = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, refreshUser } = useAuth()
  const [organizationId, setOrganizationId] = useState('')
  const [busy, setBusy] = useState(false)

  const transferQuery = useQuery({
    queryKey: ['transfer', token],
    queryFn: () => getTransferByToken(token),
    retry: false,
    enabled: token.length > 0,
  })

  const organizations = user?.organizations ?? []

  useEffect(() => {
    if (organizationId === '' && organizations.length > 0) {
      setOrganizationId(String(user?.active_organization_id ?? organizations[0]?.id ?? ''))
    }
  }, [organizationId, organizations, user?.active_organization_id])

  if (transferQuery.isLoading) {
    return <LoadingBlock label="Загружаем передачу..." />
  }

  const transfer = transferQuery.data
  const notFound = transferQuery.isError || !transfer
  const emailMatches =
    Boolean(user?.email) && transfer?.recipient_email?.toLowerCase() === user?.email?.toLowerCase()

  const onAccept = async () => {
    setBusy(true)
    try {
      await acceptTransfer(token, organizationId || undefined)
      await refreshUser()
      // Проект уехал в нашу организацию — сбрасываем кэш прошлого контекста.
      queryClient.clear()
      toast.success('Проект принят')
      navigate('/projects')
    } catch {
      // глобальный перехватчик покажет ошибку
    } finally {
      setBusy(false)
    }
  }

  const onDecline = async () => {
    setBusy(true)
    try {
      await declineTransfer(token)
      toast.success('Передача отклонена')
      navigate('/projects')
    } catch {
      // no-op
    } finally {
      setBusy(false)
    }
  }

  const isActionable = !notFound && transfer?.status === 'pending' && !transfer?.expired

  return (
    <div className="mx-auto mt-16 max-w-md px-4">
      <Card>
        <CardHeader>
          <CardTitle>Передача проекта</CardTitle>
          <CardDescription>
            {notFound
              ? 'Передача не найдена.'
              : `${transfer?.from_name ?? 'Пользователь'} передаёт вам проект «${transfer?.project_name ?? ''}» (${transfer?.recipient_email}).`}
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {transfer?.keep_previous_owner ? (
            <p className="text-sm text-[var(--color-text-soft)]">
              {transfer.from_name ?? 'Прежний владелец'} останется участником проекта после
              передачи.
            </p>
          ) : null}

          {!notFound && !isActionable ? (
            <p className="text-sm text-[var(--color-text-soft)]">
              Передача уже неактивна или истекла.
            </p>
          ) : null}

          {isActionable ? (
            isAuthenticated ? (
              emailMatches ? (
                <div className="space-y-3">
                  {organizations.length > 1 ? (
                    <Select
                      onChange={(event) => setOrganizationId(event.target.value)}
                      value={organizationId}
                    >
                      {organizations.map((organization) => (
                        <option key={organization.id} value={organization.id}>
                          {organization.name}
                        </option>
                      ))}
                    </Select>
                  ) : null}
                  <div className="flex gap-2">
                    <Button loading={busy} onClick={() => void onAccept()}>
                      Принять проект
                    </Button>
                    <Button loading={busy} onClick={() => void onDecline()} variant="secondary">
                      Отклонить
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[var(--color-text-soft)]">
                  Вы вошли под другим email. Войдите под {transfer?.recipient_email}, чтобы принять.
                </p>
              )
            ) : (
              <div className="flex gap-2">
                <Link to="/login" state={{ from: location }}>
                  <Button variant="secondary">Войти</Button>
                </Link>
                <Link to="/register" state={{ from: location }}>
                  <Button>Регистрация</Button>
                </Link>
              </div>
            )
          ) : null}
        </CardBody>
      </Card>
    </div>
  )
}
